import http from 'api/httpClient';

const BASE = '/api/v1/vendor-contracts';

// ---------- types ----------
export type Lane = {
  route: string;
  vehicle_type: string;
  rate: number | string;
  rate_basis: string;
  transit_days: number | string;
};

// what the New / Edit form sends (numbers may still be strings from inputs — the API parses them)
export type VendorContractInput = {
  vendor_name: string;
  vendor_pan: string;
  vendor_type: string;
  load_type: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  pincode: string;
  signatory_name: string;
  contact_phone: string;
  contact_email: string;
  lanes: Lane[];
  security_deposit: string;
  placement_hours: string;
  free_time_hours: string;
  detention_per_day: string;
  late_delivery_per_day: string;
  pod_delay_per_day: string;
  diesel_threshold_pct: string;
  diesel_faf_pct: string;
  diesel_base_price: string;
  diesel_base_city: string;
  diesel_base_date: string;
  non_placement_penalty: string;
  advance_pct: string;
  balance_days: string;
  valid_from: string;
  valid_to: string;
};

export type VendorContractListItem = {
  id: number;
  contract_number: string | null;
  vendor_name: string;
  vendor_pan: string;
  vendor_type: string;
  load_type: string;
  status: string;
  lane_count: number;
  first_route: string | null;
  valid_from: string;
  valid_to: string;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
};

export type VendorContractDetail = {
  id: number;
  contract_number: string | null;
  template_key: string;
  template_title: string;
  status: string;            // display status ("expired" derived)
  lifecycle_status: string;  // stored status
  vendor_name: string;
  vendor_pan: string;
  pan_status: string | null;
  pan_registered_name: string | null;
  pan_name_match: boolean | null;
  pan_verified_at: string | null;
  vendor_type: string;
  load_type: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  state: string;
  pincode: string;
  signatory_name: string;
  contact_phone: string | null;
  contact_email: string | null;
  lanes: (Lane & { line_no: number; rate: number; transit_days: number })[];
  security_deposit: number;
  placement_hours: number;
  free_time_hours: number;
  detention_per_day: number;
  late_delivery_per_day: number;
  pod_delay_per_day: number;
  diesel_threshold_pct: number;
  diesel_faf_pct: number;
  diesel_base_price: number;
  diesel_base_city: string;
  diesel_base_date: string;
  non_placement_penalty: string;
  advance_pct: number;
  balance_days: number;
  valid_from: string;
  valid_to: string;
  agreement_date: string | null;
  rejection_reason: string | null;
  has_pdf: boolean;
  signed_copy_name: string | null;
  created_by: string;
  created_at: string;
  submitted_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  executed_by: string | null;
  executed_at: string | null;
  user_names: Record<string, string>;
  can_approve: boolean;
  can_edit: boolean;
  can_upload_signed: boolean;
  approve_blocked_reason: string | null;
};

export type ActivityChange = { field: string; label: string; old: string | null; new: string | null };
export type ActivityItem = {
  id: number;
  event_type: string;
  actor_ecode: string | null;
  actor_name: string | null;
  detail: string | null;
  changes: ActivityChange[] | null;
  created_at: string;
};

export type TypeOption = { value: string; label: string };
export type FormOptions = {
  vendor_types: TypeOption[];
  load_types: TypeOption[];
  templates: { load_type: string; vendor_type: string; template_key: string; title: string }[];
};

export type PanVerifyResult = {
  pan: string;
  pan_status: string | null;
  registered_name: string | null;
  name_match: boolean | null;
  valid: boolean;
};

// ---------- calls ----------
export async function listVendorContracts(params: {
  search?: string; status?: string; vendorType?: string; loadType?: string; awaitingMe?: boolean;
  limit: number; offset: number;
}) {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.status) q.set('status', params.status);
  if (params.vendorType) q.set('vendor_type', params.vendorType);
  if (params.loadType) q.set('load_type', params.loadType);
  if (params.awaitingMe) q.set('awaiting_me', 'true');
  q.set('limit', String(params.limit));
  q.set('offset', String(params.offset));
  return http.get<{ contracts: VendorContractListItem[]; total: number }>(`${BASE}?${q.toString()}`);
}

export async function getFormOptions() {
  return http.get<FormOptions>(`${BASE}/options`);
}
export async function verifyPan(pan: string, name: string) {
  return http.post<PanVerifyResult>(`${BASE}/pan-verify`, { pan, name });
}
export async function createVendorContract(body: VendorContractInput) {
  return http.post<{ id: number; contract_number: string }>(BASE, body as unknown as Record<string, unknown>);
}
export async function resubmitVendorContract(id: number, body: VendorContractInput) {
  return http.put(`${BASE}/${id}`, body as unknown as Record<string, unknown>);
}
export async function previewUnsaved(body: VendorContractInput): Promise<Blob> {
  const blob = await http.post<Blob>(`${BASE}/preview`, body as unknown as Record<string, unknown>, { responseType: 'blob' });
  return new Blob([blob], { type: 'application/pdf' });
}
export async function getVendorContract(id: number) {
  return http.get<VendorContractDetail>(`${BASE}/${id}`);
}
export async function getActivity(id: number) {
  return http.get<{ activity: ActivityItem[] }>(`${BASE}/${id}/activity`);
}
export async function approveVendorContract(id: number) {
  return http.post(`${BASE}/${id}/approve`, {});
}
export async function rejectVendorContract(id: number, reason: string) {
  return http.post(`${BASE}/${id}/reject`, { reason });
}
// the approved agreement, or a DRAFT render before approval; `download` logs it on the activity trail
export async function fetchAgreementPdf(id: number, download = false): Promise<Blob> {
  const blob = await http.get<Blob>(`${BASE}/${id}/pdf${download ? '?download=true' : ''}`, { responseType: 'blob' });
  return new Blob([blob], { type: 'application/pdf' });
}
export async function uploadSignedCopy(id: number, file: File) {
  const form = new FormData();
  form.append('file', file);
  return http.post(`${BASE}/${id}/signed`, form);
}
const SIGNED_TYPES: Record<string, string> = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' };
export const fileExt = (name: string) => (name.split('.').pop() || '').toLowerCase();
export async function fetchSignedCopy(id: number, fileName: string, download = false): Promise<Blob> {
  const blob = await http.get<Blob>(`${BASE}/${id}/signed${download ? '?download=true' : ''}`, { responseType: 'blob' });
  return new Blob([blob], { type: SIGNED_TYPES[fileExt(fileName)] || 'application/octet-stream' });
}

export { saveBlob } from 'pages/contracts/api';

// ---------- display helpers ----------
export const STATUS_LABELS: Record<string, string> = {
  pending_approval: 'Pending approval',
  rejected: 'Rejected',
  approved: 'Approved',
  executed: 'Executed',
  expired: 'Expired',
};
export const STATUS_STYLES: Record<string, string> = {
  pending_approval: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  rejected: 'bg-red-50 text-red-700 ring-red-600/10',
  approved: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  executed: 'bg-green-50 text-green-700 ring-green-600/20',
  expired: 'bg-gray-50 text-gray-600 ring-gray-500/10',
};
export const VENDOR_TYPE_LABELS: Record<string, string> = { broker: 'Broker', vehicle_owner: 'Vehicle Owner' };

export const inr = (v: number | string | null | undefined) =>
  v === null || v === undefined || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana',
  'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
  'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi',
  'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];
